import _wsShared from '@symbo-ls/workspace-shared'
import { appendBootLangHint } from './bootLang.js'
const shellGetSDK = (...args) => _wsShared.functions.getSDK(...args)
const shellWorkspaceOrigin = (...args) => _wsShared.functions.workspaceOrigin(...args)
// Cross-origin sign-in handoff from demo.symbols.app → my.symbols.app.
//
// demo.symbols.app and my.symbols.app are DIFFERENT origins, so the real
// account tokens the SDK just minted (claimDemoAccount upgraded the demo
// account in place, or login on an existing account, or cloneDemoWorkspace
// after login) live in demo.symbols.app's localStorage and DO NOT carry
// across to my.symbols.app. To land the user signed-in on the shell, we
// hand the freshly-minted access + refresh tokens to the shell's own
// hash-token consumer page and let it persist them into my-origin
// localStorage + hydrate.
//
// TARGET PAGE — `/auth/magic-callback` (NOT `/_session-bridge`).
//   `/_session-bridge` (pages/session-bridge.js) is a postMessage RESPONDER:
//   it hands tokens OUT to iframe parents via window.postMessage and has NO
//   URL-hash consumption — redirecting there with `#tokens` would do nothing
//   and the user would arrive unauthenticated. The shell's genuine, already
//   built cross-origin token-hop consumer is `/auth/magic-callback`
//   (shared/pages/auth/magic-callback.js): it parses
//     #access_token=…&refresh_token=…&access_exp=<ms>&redirect=<path>
//   applies the tokens via the SDK TokenManager, hydrates via getMe(), scrubs
//   the hash, and routes to `redirect`. Both pages are allow-listed for
//   guests in GUEST_ROUTE_RE, so the unauthenticated cross-origin bounce
//   renders without an auth gate either way — but only magic-callback signs
//   the user in. Tokens ride the URL HASH (not query) so they never hit
//   server logs or Referer headers during the bounce.
//
// Invoked from DemoClaimBanner via `el.call('demoHandoff')` (no imports
// between project files). Reads the CURRENT session through the public SDK
// surface `getSession()` ({ access_token, refresh_token, expires_at }) — no
// localStorage reach-in. Resolves the shell origin via `workspaceOrigin()`
// (prod → https://my.symbols.app, dev → dev.my / my.localhost) so the host
// is never hardcoded. Returns true once it triggers the redirect, false when
// there's no usable token to hand off (caller surfaces an error).
export const demoHandoff = async () => {
  if (typeof window === 'undefined') return false

  const sdk = shellGetSDK()
  const session = await sdk.getSession()
  const accessToken = session?.access_token
  if (!accessToken) return false

  const refreshToken = session?.refresh_token || ''
  // `expires_at` is the TokenManager's epoch-ms expiry; magic-callback reads
  // `access_exp` as epoch-ms and derives `expires_in` from it. Pass it through
  // when present so the persisted token carries an accurate lifetime.
  const accessExp = Number(session?.expires_at) || 0

  const params = new URLSearchParams()
  params.set('access_token', accessToken)
  if (refreshToken) params.set('refresh_token', refreshToken)
  if (accessExp) params.set('access_exp', String(accessExp))
  // Land the user on the shell home after the callback persists the session.
  params.set('redirect', '/')

  const origin = shellWorkspaceOrigin() || 'https://my.symbols.app'
  // ARRIVAL LANGUAGE HINT (WS-ARRIVAL-URL-CARRIES-NO-LANGUAGE-HINT-1). The
  // shell's own boot loader resolves its FIRST-PAINT language from `?lang=`,
  // then from two caches keyed by HOST. demo.symbols.app and my.symbols.app
  // are different hosts, so neither cache has an entry there and the landing
  // frame is English — even though this origin runs the whole shell and
  // resolved the demo workspace's language long before the claim click.
  //
  // `__smblsBootLangPublished` is the RESOLVED language and only that: the
  // head script's own first-paint guess writes `__smblsBootLang`, while
  // app.js `_publishBootLang(root.lang)` is the sole writer of this key. So
  // a shell that never settled a language stamps nothing and the destination
  // falls through to its own levels — a guessed hint is worse than none.
  const target = appendBootLangHint(
    `${origin}/auth/magic-callback#${params.toString()}`,
    window.__smblsBootLangPublished
  )

  // Full cross-origin navigation (NOT el.router) — a different origin must do
  // a hard load so the shell boots fresh and magic-callback's onRender reads
  // the hash before the SDK TokenManager hydrates from (empty) my-origin
  // localStorage.
  window.location.assign(target)
  return true
}
